/**
 * The Flyout component is used to display content in the flyout section
 *
 * @module views/components/Flyout
 * @memberof -Common
 */
import React, { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { isServer } from '../../../utils/device_detection/device_detection';
import './Flyout.scss';
import Icon from '../Icon/Icon'
import PropTypes from 'prop-types';

export const detectFlyoutState = ( [openMutation] ) => {
  if( openMutation.oldValue === null ){
    document.body.classList.add( 'Flyout-blocked' );
  }
  else {
    document.body.classList.remove( 'Flyout-blocked' );
  }
};

/**
 * Represents a Flyout component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns Flyout
 */

const Flyout = forwardRef( ( { children, opener, flyoutAlign, callBackFunc, closeAccessibilityAction }, flyoutRef ) => {

  const ref = flyoutRef;
  if( typeof HTMLDialogElement !== 'function' ){
    import( 'dialog-polyfill' ).then( ( { default: { registerDialog } } ) => registerDialog( ref.current ) );
  }

  useEffect( () => {
    const dialogObserver = new MutationObserver( detectFlyoutState );
    dialogObserver.observe( ref.current, { attributeFilter: ['open'], attributeOldValue: true } );
    /**
     * creating custom event listener for closing the flyout to support in safari browswer
     */

    if( ref.current ){
      ref.current.onclose = e => {
        opener.current.focus();
        if( callBackFunc ){
          callBackFunc();
        }
      }
    }
    document.onclick = ( e ) => {
      // This prevents issues with forms
      if( e.target.tagName !== 'DIALOG' ){
        return
      }
      const rect = e.target.getBoundingClientRect();

      const clickedInDialog = (
        rect.top <= e.clientY &&
          e.clientY <= rect.top + rect.height &&
          rect.left <= e.clientX &&
          e.clientX <= rect.left + rect.width
      );

      if( clickedInDialog === false ){
        e.target.close();
      }
    }
    return () => dialogObserver.disconnect();
  }, [] );

  return !isServer() ? createPortal( (
    <dialog
      ref={ ref }
      className={ classNames( `Flyout`, {
        ...( flyoutAlign && { [`Flyout--${flyoutAlign}`]: flyoutAlign } ) } ) }
    >
      { children }
      <form method='dialog'>
        <button className='Flyout__close'
          aria-label={ closeAccessibilityAction?.label }
          type='submit'
        >
          <Icon
            size='s'
            name={ 'X' }
          />
        </button>
      </form>
    </dialog>
  ), document.body ) : null;
} )


/**
  * Property type definitions
  * @type {object}
  * @property {object} closeAccessibilityAction - Sets close accessibility label and graphql
  */
export const propTypes = {
  /** Sets close accessibility label and graphql */
  closeAccessibilityAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } )
}

Flyout.propTypes = propTypes;

export default Flyout;